import React, { useContext, useState, useEffect } from "react"
import { auth } from "../firebase"
import firebase from "firebase/app";


interface AuthValue {
  currentUser: firebase.User | null,
  currentUserRole: string,
  currentUserTrusted: boolean,
  login: (email: string, password: string) => Promise<firebase.auth.UserCredential>,
  logout: () => Promise<void>,
  signup: (email: string, password: string) => Promise<firebase.auth.UserCredential>,
  resetPassword: (email: string) => Promise<void>,
  updateEmail: (email: string) => Promise<void>,
  updatePassword: (password: string) => Promise<void>,
  updateDisplayName: (displayName: string) => Promise<void>
  getRecaptchaVerifier: (id: string) => firebase.auth.RecaptchaVerifier
  logInWithPhoneNumber: (phoneNumber: string, applicationVerifier: firebase.auth.RecaptchaVerifier) => Promise<firebase.auth.ConfirmationResult>
}

const AuthContext = React.createContext<AuthValue | null>(null);

export function useAuth() {
  return useContext(AuthContext)
}

export const AuthProvider: React.FC = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<firebase.User | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentUserRole, setCurrentUserRole] = useState<string>('user')
  const [currentUserTrusted, setCurrentUserTrusted] = useState(false);

  function login(email: string, password: string) {
    return auth.signInWithEmailAndPassword(email, password)
  }

  function getRecaptchaVerifier(id: string) {
    return new firebase.auth.RecaptchaVerifier(id, {
      'size': 'invisible',
    });
  }

  function logInWithPhoneNumber(phoneNumber: string, applicationVerifier: firebase.auth.RecaptchaVerifier) {
    return firebase.auth().signInWithPhoneNumber(phoneNumber, applicationVerifier);
  }

  function logout() {
    return auth.signOut()
  }

  function signup(email: string, password: string) {
    return auth.createUserWithEmailAndPassword(email, password)
  }

  function resetPassword(email: string) {
    return auth.sendPasswordResetEmail(email)
  }

  function updateEmail(email: string) {
    return currentUser!.updateEmail(email)
  }

  function updatePassword(password: string) {
    return currentUser!.updatePassword(password)
  }

  function updateDisplayName(displayName: string) {
    return currentUser!.updateProfile({ displayName: displayName })
  }

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      setCurrentUser(user);
      const getIdTokenResult = async () => {
        const idTokenResult = await auth?.currentUser?.getIdTokenResult();
        if (idTokenResult) {
          setCurrentUserRole(idTokenResult?.claims.role);
          setCurrentUserTrusted(idTokenResult?.claims.trusted);
        }
        else
          setCurrentUserRole('');
      }
      getIdTokenResult();
      setLoading(false);
    });

    return unsubscribe;
  }, [])

  const value = {
    currentUser,
    currentUserRole,
    currentUserTrusted,
    login,
    logout,
    signup,
    resetPassword,
    updateEmail,
    updatePassword,
    updateDisplayName,
    getRecaptchaVerifier,
    logInWithPhoneNumber
  }

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  )
}